import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import LoadingSpinner from '../../shared/spinner';

interface PaginationProps {
  totalCount: number;
  itemsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
     totalCount,
     itemsPerPage,
     currentPage,
     onPageChange,
     isLoading
   }) => {
  // Keep the last valid total pages count to prevent UI collapse during loading
  const [lastValidTotalPages, setLastValidTotalPages] = React.useState(Math.ceil(totalCount / itemsPerPage));
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  React.useEffect(() => {
    if (!isLoading && totalPages > 0) {
      setLastValidTotalPages(totalPages);
    }
  }, [totalPages, isLoading]);

  // Generate page numbers using the stored last valid total
  const getPageNumbers = () => {
    const pageNumbers: number[] = [];
    const maxVisiblePages = 5;
    const pagesCount = isLoading ? lastValidTotalPages : totalPages;

    if (pagesCount <= maxVisiblePages) {
      for (let i = 1; i <= pagesCount; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(pagesCount, startPage + maxVisiblePages - 1);

      if (endPage === pagesCount) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  const start = ((currentPage - 1) * itemsPerPage) + 1;
  const end = Math.min(currentPage * itemsPerPage, totalCount);

  return (
    <div className="flex flex-col items-center gap-3 border-t border-gray-200 py-8 mt-10">
      <div className="relative flex items-center bg-gray-50 rounded-lg shadow-sm overflow-hidden">
        {/* Previous button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
          className="p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[40px] flex justify-center"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>

        {/* Page numbers */}
        <div className="flex border-l border-r border-gray-200">
          {getPageNumbers().map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium transition-colors min-w-[40px]
                ${Number(currentPage) === pageNum
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'text-gray-700 hover:bg-gray-100'
              } ${isLoading ? 'cursor-not-allowed' : ''}`}
            >
              {pageNum}
            </button>
          ))}
        </div>

        {/* Next button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === (isLoading ? lastValidTotalPages : totalPages) || isLoading}
          className="p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[40px] flex justify-center"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Results counter */}
      <div className="text-sm text-gray-600 min-w-[200px] text-center">
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <LoadingSpinner className="w-4 h-4" />
            Loading results...
          </span>
        ) : (
          <>
            Showing {start} to {end} of {totalCount} results
          </>
        )}
      </div>
    </div>
  );
};

export default Pagination;